import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trackNavigation, trackClick } from '../lib/utils';
import {
  LayoutDashboard,
  Package,
  FolderOpen,
  MapPin,
  Users,
  ArrowLeftRight,
  Calendar,
  BarChart3,
  Shield,
  FileText,
  LogOut,
  Menu,
  X,
  ChevronRight,
  User,
  ChevronDown,
  Palette,
  Key,
  HelpCircle,
  Download,
  Megaphone,
  Database,
  Mail,
  Bug,
  ClipboardList,
  Globe,
  Radar,
  History,
  BookOpen,
  FileSpreadsheet,
  FileCheck,
  QrCode,
  Smartphone,
  Wrench,
  CalendarDays,
  FileBarChart,
  Building2,
  Sun,
  Moon,
  RefreshCw,
  Clock,
  GitMerge,
  UserCheck,
  Bell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';
import { getRoleLabel } from '../lib/utils';
import { NotificationCenter } from './notifications';
import { Badge } from '../components/ui/badge';

// Permission mapping for menu items
const menuPermissions = {
  '/dashboard': 'view_dashboard',
  '/inventory': 'view_inventory',
  '/categories': 'view_categories',
  '/locations': 'view_locations',
  '/responsibles': 'view_responsibles',
  '/movements': 'view_movements',
  '/equipment-tracking': 'view_equipment_tracking',
  '/calendar': 'view_calendar',
  '/reports': 'view_reports',
  '/maintenance': 'view_maintenance',
  '/reservations': 'view_reservations',
  '/clients': 'view_clients',
  '/scheduled-reports': 'view_reports',
  '/responsivas': 'view_responsivas',
  '/etiquetas': 'view_etiquetas',
  '/templates': 'view_templates',
  '/manual': null, // Available to all users
  '/faq': null, // Available to all users
  '/version': null, // Available to all users
  '/operations': 'view_operations',
  '/users': 'view_users',
  '/permissions': 'manage_permissions',
  '/audit': 'view_audit',
  '/style': 'manage_style',
  '/backup': null, // superAdminOnly - no permission check
  '/email': null, // superAdminOnly - no permission check
  '/testeo': null, // adminOnly - no permission check
  '/integraciones': null, // superAdminOnly - no permission check
  '/debugger': null, // superAdminOnly - no permission check
  '/documentacion': null, // superAdminOnly - no permission check
  '/announcements': 'manage_announcements'
};

const allMenuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: 'view_dashboard' },
  { path: '/inventory', label: 'Inventario', icon: Package, permission: 'view_inventory' },
  { path: '/categories', label: 'Categorías', icon: FolderOpen, permission: 'view_categories' },
  { path: '/locations', label: 'Ubicaciones', icon: MapPin, permission: 'view_locations' },
  { path: '/responsibles', label: 'Responsables', icon: Users, permission: 'view_responsibles' },
  { path: '/movements', label: 'Movimientos', icon: ArrowLeftRight, permission: 'view_movements' },
  { path: '/responsivas', label: 'Responsivas', icon: FileCheck, permission: 'view_responsivas' },
  { path: '/etiquetas', label: 'Etiquetas QR', icon: QrCode, permission: 'view_etiquetas' },
  { path: '/calendar', label: 'Calendario', icon: Calendar, permission: 'view_calendar' },
  { path: '/maintenance', label: 'Mantenimiento', icon: Wrench, permission: 'view_maintenance' },
  { path: '/reservations', label: 'Reservas', icon: CalendarDays, permission: 'view_reservations' },
  { path: '/clients', label: 'Clientes', icon: Building2, permission: 'view_clients' },
  { path: '/reports', label: 'Reportes', icon: BarChart3, permission: 'view_reports' },
  { path: '/manual', label: 'Manual', icon: HelpCircle },
  { path: '/faq', label: 'Ayuda', icon: HelpCircle },
  { path: '/version', label: 'Versión', icon: History },
];

// Admin menu items - requires admin/superadmin role OR specific permission
const allAdminMenuItems = [
  { path: '/operations', label: 'Operaciones', icon: Radar, permission: 'view_operations' },
  { path: '/users', label: 'Usuarios', icon: Shield, permission: 'view_users' },
  { path: '/permissions', label: 'Permisos', icon: Key, permission: 'manage_permissions', superAdminOnly: true },
  { path: '/equipment-tracking', label: 'Rastreo', icon: Radar, permission: 'view_equipment_tracking' },
  { path: '/templates', label: 'Plantillas', icon: FileSpreadsheet, permission: 'view_templates' },
  { path: '/announcements', label: 'Anuncios', icon: Megaphone, permission: 'manage_announcements' },
  { path: '/scheduled-reports', label: 'Rep. Automáticos', icon: FileBarChart, permission: 'view_reports' },
  { path: '/email-schedule', label: 'Horarios de Correo', icon: Clock, superAdminOnly: true },
  { path: '/notifications', label: 'Notificaciones', icon: Bell, adminOnly: true },
  { path: '/category-merge', label: 'Fusionar Duplicados', icon: GitMerge, superAdminOnly: true },
  { path: '/owner-assignment', label: 'Asignación de Owner', icon: UserCheck, adminOnly: true },
  { path: '/audit', label: 'Auditoría', icon: FileText, permission: 'view_audit' },
  { path: '/email', label: 'Correos', icon: Mail, superAdminOnly: true },
  { path: '/backup', label: 'Backup', icon: Database, superAdminOnly: true },
  { path: '/testeo', label: 'Testeo', icon: ClipboardList, adminOnly: true },
  { path: '/integraciones', label: 'API Externa', icon: Globe, superAdminOnly: true },
  { path: '/debugger', label: 'Debugger', icon: Bug, superAdminOnly: true },
  { path: '/style', label: 'Estilo', icon: Palette, superAdminOnly: true },
  { path: '/documentacion', label: 'Documentación', icon: BookOpen, superAdminOnly: true },
  { path: '/android', label: 'Android', icon: Smartphone, superAdminOnly: true },
];

export const Layout = ({ children }) => {
  const { user, logout, hasPermission, isSuperAdmin, isAdmin, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [criticalCounts, setCriticalCounts] = useState({ falla: 0, perdido: 0, total_critical: 0 });
  const [styleConfig, setStyleConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('app_style_config');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  // Load style config from server on mount
  useEffect(() => {
    const loadStyleConfig = async () => {
      try {
        const res = await api().get('/config/style');
        if (res.data && Object.keys(res.data).length > 0) {
          setStyleConfig(res.data);
          localStorage.setItem('app_style_config', JSON.stringify(res.data));
        }
      } catch (err) {
        // Use localStorage fallback silently
      }
    };
    loadStyleConfig();
  }, [api]);

  // P3: Load critical counts for sidebar badge
  useEffect(() => {
    const loadCriticalCounts = async () => {
      try {
        const res = await api().get('/dashboard/critical-counts');
        setCriticalCounts(res.data);
      } catch {}
    };
    loadCriticalCounts();
    const interval = setInterval(loadCriticalCounts, 60000);
    return () => clearInterval(interval);
  }, [api]);

  // Listen for style config changes (when saved from StylePage)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'app_style_config' && e.newValue) {
        try { setStyleConfig(JSON.parse(e.newValue)); } catch {}
      }
    };
    window.addEventListener('storage', onStorage);

    // Also poll localStorage for same-tab updates
    const interval = setInterval(() => {
      try {
        const saved = localStorage.getItem('app_style_config');
        if (saved) {
          const parsed = JSON.parse(saved);
          setStyleConfig(prev => {
            if (JSON.stringify(prev) !== saved) return parsed;
            return prev;
          });
        }
      } catch {}
    }, 2000);

    return () => {
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, []);

  const appName = styleConfig?.app_name || 'Entradas y Salidas de Equipo';
  const logoUrl = styleConfig?.logo_url || 'https://cmidiomas.com/images/logo-default-246x44.png';
  const logoSize = parseInt(styleConfig?.logo_size) || 48;

  // Get admin section name based on user role
  const getAdminSectionName = () => {
    if (!user) return 'Panel';
    const role = user.role?.toLowerCase();
    
    if (role === 'superadmin' || role === 'admin') {
      return 'Administración';
    } else if (role === 'supervisor') {
      return 'Supervisión';
    } else if (role === 'auditor') {
      return 'Auditoría';
    } else if (role === 'user' || role === 'usuario') {
      return 'Mi Panel';
    } else {
      return 'Herramientas';
    }
  };

  // Filter menu items based on permissions
  const menuItems = allMenuItems.filter(item => {
    // If item has no permission defined, show to all authenticated users
    if (!item.permission) return true;
    // Otherwise check permission
    return hasPermission(item.permission);
  });
  
  // Filter admin menu items - strictly based on permissions
  const adminMenuItems = allAdminMenuItems.filter(item => {
    // SuperAdmin only items - only superadmin can see
    if (item.superAdminOnly) {
      return isSuperAdmin();
    }
    // Check specific permission
    if (item.permission) {
      return hasPermission(item.permission);
    }
    // Check menu permissions map
    const perm = menuPermissions[item.path];
    if (perm) {
      return hasPermission(perm);
    }
    // Default: only admin/superadmin can see admin items
    return isAdmin() || isSuperAdmin();
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  const renderMenuItem = ({ path, label, icon: Icon }) => (
    <Link
      key={path}
      to={path}
      onClick={() => {
        setSidebarOpen(false);
        trackNavigation(path, label);
      }}
      className={cn(
        'flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200',
        isActive(path)
          ? 'bg-primary/10 text-primary border-l-2 border-primary'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
      )}
    >
      <Icon className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1">{label}</span>
      {path === '/inventory' && criticalCounts.total_critical > 0 && (
        <Badge
          variant="destructive"
          className="h-5 min-w-5 flex items-center justify-center px-1.5 text-[10px] font-bold animate-pulse"
          data-testid="critical-badge-inventory"
        >
          {criticalCounts.total_critical}
        </Badge>
      )}
      {path === '/movements' && criticalCounts.overdue_returns > 0 && (
        <Badge
          className="h-5 min-w-5 flex items-center justify-center px-1.5 text-[10px] font-bold bg-red-500/20 text-red-400 border-red-500/30 animate-pulse"
          data-testid="overdue-returns-badge"
        >
          {criticalCounts.overdue_returns}
        </Badge>
      )}
      {path === '/maintenance' && criticalCounts.falla > 0 && (
        <Badge
          className="h-5 min-w-5 flex items-center justify-center px-1.5 text-[10px] font-bold bg-amber-500/20 text-amber-400 border-amber-500/30"
          data-testid="critical-badge-maintenance"
        >
          {criticalCounts.falla}
        </Badge>
      )}
    </Link>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-card/80 backdrop-blur-md border-b border-border/40 z-50 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-btn"
        >
          {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
        <img 
          src={logoUrl} 
          alt="Logo" 
          className="h-5 w-auto"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div className="w-10" />
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Simplified without user info */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-screen w-56 bg-card/95 backdrop-blur-xl border-r border-border/40 z-50 transition-transform duration-300 lg:translate-x-0 flex flex-col',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Logo */}
        <div className="flex flex-col items-center justify-center px-3 border-b border-border/40 flex-shrink-0 py-3">
          <img 
            src={logoUrl} 
            alt="Logo" 
            className="w-auto mb-1"
            style={{ height: `${logoSize}px`, maxWidth: '90%', objectFit: 'contain' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div className="text-center leading-tight">
            <span className="text-primary font-semibold text-xs">{appName}</span>
          </div>
        </div>

        {/* Navigation - Scrollable area */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="mb-3">
            <span className="px-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
              Principal
            </span>
          </div>
          {menuItems.map(renderMenuItem)}

          {adminMenuItems.length > 0 && (
            <>
              <div className="my-3 pt-3 border-t border-border/40">
                <span className="px-3 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                  {getAdminSectionName()}
                </span>
              </div>
              {adminMenuItems.map(renderMenuItem)}
            </>
          )}
          {/* Bottom padding for mobile safe area */}
          <div className="pb-16 lg:pb-4" />
        </nav>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-56 min-h-screen">
        {/* Header with User Info */}
        <header className="sticky top-0 lg:top-0 h-14 bg-background/80 backdrop-blur-md border-b border-border/40 z-30 flex items-center justify-between px-4 lg:px-6 mt-14 lg:mt-0">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/dashboard" className="hover:text-foreground transition-colors">
              Inicio
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-foreground font-medium">
              {menuItems.find(item => item.path === location.pathname)?.label ||
               adminMenuItems.find(item => item.path === location.pathname)?.label ||
               'Página'}
            </span>
          </div>

          {/* Notifications & User Menu */}
          <div className="flex items-center gap-2">
            {/* Theme Mode Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => {
                const newMode = document.documentElement.classList.contains('light') ? 'dark' : 'light';
                if (newMode === 'light') {
                  document.documentElement.classList.add('light');
                } else {
                  document.documentElement.classList.remove('light');
                }
                localStorage.setItem('themeMode', newMode);
                window.dispatchEvent(new CustomEvent('themeModeChange', { detail: newMode }));
              }}
              data-testid="theme-mode-toggle"
            >
              {document.documentElement.classList.contains('light')
                ? <Moon className="h-4 w-4" />
                : <Sun className="h-4 w-4" />}
            </Button>

            {/* Notification Bell */}
            <NotificationCenter />

            {/* Hard Reset - force reload without cache */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              title="Recargar (forzar actualización)"
              onClick={() => {
                try {
                  trackClick('hard-reset-btn', 'Recargar app');
                } catch {}
                // Attempt to clear cached service worker if any, then hard reload
                if ('caches' in window) {
                  caches.keys().then(keys => {
                    keys.forEach(k => caches.delete(k));
                  }).finally(() => window.location.reload(true));
                } else {
                  window.location.reload(true);
                }
              }}
              data-testid="hard-reset-btn"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2 h-9" data-testid="user-menu-btn">
                  {user?.avatar_url ? (
                    <img 
                      src={user.avatar_url} 
                      alt={user?.name} 
                      className="w-7 h-7 rounded-full object-cover ring-2 ring-primary/30"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                      <span className="text-primary font-medium text-xs">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                  <div className="hidden sm:block text-left">
                    <p className="text-xs font-medium leading-tight">{user?.name}</p>
                    <p className="text-[10px] text-muted-foreground leading-tight">{getRoleLabel(user?.role)}</p>
                  </div>
                  <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 flex items-center gap-2">
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  ) : null}
                  <div>
                    <p className="text-sm font-medium">{user?.name}</p>
                    <p className="text-xs text-muted-foreground">{getRoleLabel(user?.role)}</p>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to="/profile" className="flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Mi Perfil
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/faq" className="cursor-pointer">
                    <HelpCircle className="h-4 w-4 mr-2" />
                    Ayuda
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
